// covered-by: frontends/carrie/src/components/ask-puntt-review/__tests__/reviewCommentDisplay.test.ts
import { type AskPunttReviewComment } from "@mg/schemas/src/christo/askPunttReview";

import {
  commentSectionLabelKey,
  extractRiskSection,
  parseCommentSections,
  slugifyCommentSectionLabel,
  type CommentSection,
} from "../../routes/ask-puntt/utils/parseCommentSections";

export { commentSectionLabelKey };

function stripMarkdownInline(text: string): string {
  return text.replace(/\*\*/g, "").replace(/`/g, "").trim();
}

function firstLine(text: string): string {
  return text.trim().split("\n")[0]?.trim() ?? "";
}

function issueLine(sections: CommentSection[]): string | null {
  const issue = sections.find(
    (section) => slugifyCommentSectionLabel(section.label) === "issue",
  );
  if (!issue?.body.trim()) return null;
  return stripMarkdownInline(firstLine(issue.body));
}

/** The comment's Issue line, or null when its description has none. */
export function reviewCommentIssue(
  comment: AskPunttReviewComment,
): string | null {
  return issueLine(parseCommentSections(comment.description)) || null;
}

export function reviewCommentTitle(comment: AskPunttReviewComment): string {
  // why `title` before `rule`: `rule` is the spec quote, and two findings on
  // one spec sentence would carry the same title.
  if (comment.title?.trim()) return comment.title.trim();
  if (comment.rule?.trim()) return comment.rule.trim();

  const parsed = parseCommentSections(comment.description);
  const { risk } = extractRiskSection(parsed);
  if (risk?.body.trim()) {
    return stripMarkdownInline(firstLine(risk.body));
  }

  const issue = issueLine(parsed);
  if (issue != null) return issue || "Finding";

  const first = firstLine(comment.description);
  if (!first) return "Finding";
  return stripMarkdownInline(first.replace(/^\*\*[^*]+:\*\*\s*/, ""));
}

/** The spec quote to show under the card, only when the title is not already it. */
export function reviewCommentSpecQuote(
  comment: AskPunttReviewComment,
): string | null {
  if (!comment.title?.trim()) return null;
  return comment.rule?.trim() || null;
}

export function reviewCommentCollapsedSections(
  description: string,
): CommentSection[] {
  const parsed = parseCommentSections(description);
  const { sections } = extractRiskSection(parsed);
  return sections.filter((section) => {
    const slug = slugifyCommentSectionLabel(section.label);
    return slug === "issue" || slug === "fix";
  });
}

export function formatReviewCommentTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}

export type ReviewCommentAuthorOptions = {
  aiLabel: string;
  currentUserId?: string;
  currentUserName?: string;
  authorNameByUserId?: Readonly<Record<string, string>>;
  unknownAuthorLabel: string;
};

export function reviewCommentAuthorLabel(
  comment: AskPunttReviewComment,
  options: ReviewCommentAuthorOptions,
): string {
  const isAiComment = comment.isAI || Boolean(comment.aiPersona?.name?.trim());
  if (isAiComment) return options.aiLabel;

  const createdBy = comment.createdBy?.trim();
  if (createdBy && options.authorNameByUserId?.[createdBy]?.trim()) {
    return options.authorNameByUserId[createdBy].trim();
  }
  if (
    createdBy &&
    options.currentUserId &&
    createdBy === options.currentUserId &&
    options.currentUserName?.trim()
  ) {
    return options.currentUserName.trim();
  }

  return options.unknownAuthorLabel;
}

export function buildCommentNumberById(
  comments: readonly AskPunttReviewComment[],
): Record<string, number> {
  const map: Record<string, number> = {};
  comments
    .filter((comment) => !comment.isDeleted)
    .forEach((comment, index) => {
      map[comment._id] = index + 1;
    });
  return map;
}
